import { useAllergies } from "@/hooks/features/use-allergies";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { IonContent, IonPage } from "@ionic/react";
import { useMemo } from "react";
import { OnboardingNextFab } from "../../components/onboarding-next-fab";
import { OnboardingStepShell } from "../../components/onboarding-step-shell";
import { SelectableChip } from "../../components/selectable-chip";
import { setMixpanelOnboardingEvent } from "../../utils/mixpanel-onboarding";
import { setSmartlookOnboardingEvent } from "../../utils/smartlook-onboarding";

const NONE_OPTION = "None";

const OnboardingAllergiesPage = () => {
  const { info, navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const { sortedAllergyList, selectedAllergies, setSelectedAllergies, handleSubmit, isUpdating } =
    useAllergies(() => {
      setSmartlookOnboardingEvent({ type: "submit", stepPath: info?.path });
      setMixpanelOnboardingEvent({ type: "submit", stepPath: info?.path });
      navigateToNextStep();
    });

  const orderedList = useMemo(() => {
    if (!sortedAllergyList.includes(NONE_OPTION)) return sortedAllergyList;
    const rest = sortedAllergyList.filter((a) => a !== NONE_OPTION);
    return [NONE_OPTION, ...rest];
  }, [sortedAllergyList]);

  const toggle = (option: string) => {
    setSelectedAllergies((prev) => {
      const next = new Set(prev);
      if (option === NONE_OPTION) {
        if (next.has(NONE_OPTION)) {
          next.delete(NONE_OPTION);
        } else {
          next.clear();
          next.add(NONE_OPTION);
        }
        return next;
      }
      if (next.has(option)) {
        next.delete(option);
      } else {
        next.delete(NONE_OPTION);
        next.add(option);
      }
      return next;
    });
  };

  const onAdvance = async () => {
    if (isUpdating) return;
    await preloadNextRoute();
    handleSubmit();
  };

  return (
    <IonPage>
      <IonContent fullscreen>
        <OnboardingStepShell
          icon="/assets/images/onboarding-icons/allergies.svg"
          title="Do you have any allergies or intolerances?"
          subtitle="Select all that apply"
        >
          <div className="flex w-full max-w-[321px] grid grid-cols-2 gap-4">
            {orderedList.map((option) => (
              <SelectableChip
                key={option}
                label={option}
                selected={selectedAllergies.has(option)}
                onClick={() => toggle(option)}
                disabled={isUpdating}
              />
            ))}
          </div>
          <OnboardingNextFab onClick={onAdvance} disabled={isUpdating} />
        </OnboardingStepShell>
      </IonContent>
    </IonPage>
  );
};

export default OnboardingAllergiesPage;
